import { useQuery } from "@apollo/client/react";
import { VideoPlaybackDocument } from "@/graphql/media.generated";
import { VideoPlayer, Text, Mono } from "@athena/ui";

/**
 * Admin-side player for a VIDEO content item. Resolves signed HLS playback via
 * `videoPlayback(contentId)` (slice 4.1.3). Falls back to a legacy/external
 * `videoUrl` when the item has no processed asset, and shows a status message
 * while an asset is still transcoding or if it failed.
 */

type AssetStatus = "UPLOADING" | "PROCESSING" | "READY" | "FAILED" | "DELETED";

export interface CourseVideoPlayerProps {
    contentId: string;
    /** Legacy/external direct video URL, if any. */
    videoUrl?: string | null;
    /** Poster/thumbnail to show before play. */
    posterUrl?: string | null;
    /** Status of the associated processed asset, if the item has one. */
    assetStatus?: AssetStatus | null;
}

const Message = ({ children }: { children: React.ReactNode }) => (
    <div className="flex h-full w-full items-center justify-center rounded-lg bg-black">
        <Text muted size="sm">
            {children}
        </Text>
    </div>
);

export function CourseVideoPlayer({ contentId, videoUrl, posterUrl, assetStatus }: CourseVideoPlayerProps) {
    // Only fetch signed playback when there's a processed asset to play.
    const hasAsset = assetStatus != null && assetStatus !== "DELETED";
    const { data, loading } = useQuery(VideoPlaybackDocument, {
        variables: { contentId },
        skip: !hasAsset,
    });

    const playback = data?.videoPlayback;
    if (playback) {
        return (
            <VideoPlayer
                src={playback.manifestUrl}
                poster={playback.posterUrl ?? posterUrl}
                captions={playback.captions.map((c) => ({ language: c.language, src: c.vttUrl }))}
            />
        );
    }

    if (hasAsset && loading) return <Message>Loading video…</Message>;

    if (hasAsset && assetStatus !== "READY") {
        return (
            <Message>
                {assetStatus === "FAILED" ? "This video is unavailable." : "This video is still processing."}
            </Message>
        );
    }

    // No processed asset — play a legacy/external URL directly if present.
    if (videoUrl) {
        return <VideoPlayer src={videoUrl} poster={posterUrl} />;
    }

    return (
        <Message>
            <Mono as="span" size="xs" className="tracking-widest uppercase">
                No video
            </Mono>
        </Message>
    );
}
